import { bioThemes } from '../bioThemes'
import type { WizardState } from './useWizardState'

interface Props {
  state: WizardState
  onChange: (partial: Partial<WizardState>) => void
  onGoToStep: (step: number) => void
  onSubmit: () => void
  isSubmitting: boolean
  error: string | null
}

export function WizardStepReview({ state, onChange, onGoToStep, onSubmit, isSubmitting, error }: Props) {
  const themeName = (bioThemes[state.theme] || bioThemes.default).name

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Review & create
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Everything look good? You can always edit after creating.
      </p>

      <div className="space-y-4 max-w-md">
        {/* Profile Summary */}
        <div className="p-4 bg-white dark:bg-[#1c2230] rounded-xl border border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Profile</h3>
            <button
              onClick={() => onGoToStep(2)}
              className="text-xs text-[#2B6CEE] hover:underline font-medium"
            >
              Edit
            </button>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Name</span>
              <span className="text-gray-900 dark:text-white font-medium">{state.display_name || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Username</span>
              <span className="text-gray-900 dark:text-white font-medium">@{state.handle || '—'}</span>
            </div>
            {state.bio && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Bio</span>
                <span className="text-gray-900 dark:text-white font-medium truncate max-w-[200px]">{state.bio}</span>
              </div>
            )}
          </div>
        </div>

        {/* Appearance Summary */}
        <div className="p-4 bg-white dark:bg-[#1c2230] rounded-xl border border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Appearance</h3>
            <button
              onClick={() => onGoToStep(3)}
              className="text-xs text-[#2B6CEE] hover:underline font-medium"
            >
              Edit
            </button>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Theme</span>
              <span className="text-gray-900 dark:text-white font-medium">{themeName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Button style</span>
              <span className="text-gray-900 dark:text-white font-medium capitalize">{state.button_style}</span>
            </div>
          </div>
        </div>

        {/* Links Summary */}
        <div className="p-4 bg-white dark:bg-[#1c2230] rounded-xl border border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Links</h3>
            <button
              onClick={() => onGoToStep(4)}
              className="text-xs text-[#2B6CEE] hover:underline font-medium"
            >
              Edit
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {state.links.length === 0
              ? 'No starter links — you can add them in the editor.'
              : `${state.links.length} link${state.links.length !== 1 ? 's' : ''} will be created.`}
          </p>
        </div>

        {/* Publish Toggle */}
        <div className="p-4 bg-white dark:bg-[#1c2230] rounded-xl border border-gray-200 dark:border-white/10">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Publish immediately</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {state.is_published ? 'Your page will be live right away.' : 'Your page will be saved as a draft.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={state.is_published}
              onClick={() => onChange({ is_published: !state.is_published })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                state.is_published ? 'bg-[#2B6CEE]' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  state.is_published ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>

        {/* Submit */}
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="w-full py-3 bg-[#2B6CEE] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20 transition-all"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </span>
          ) : (
            'Create Bio Page'
          )}
        </button>

        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}
      </div>
    </div>
  )
}
